import type { MouseEvent } from "react";
import {
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "~/components/ui/custom-radix-components/pagination";

type PaginationProps = {
  currentPage: number;
  handlePageChange: (page: number) => void;
  totalPages: number;
  /**
   * Returns a crawlable href for the given page (e.g. `/path?page=2`).
   * Defaults to a relative `?page=N` URL so callers without path context still emit real links.
   */
  getPageHref?: (page: number) => string;
};

const defaultPageHref = (page: number) => (page > 1 ? `?page=${page}` : ".");

const Pagination = ({
  currentPage,
  handlePageChange,
  totalPages,
  getPageHref = defaultPageHref,
}: PaginationProps) => {
  const goToPage = (page: number) => (event: MouseEvent) => {
    event.preventDefault();
    handlePageChange(page);
  };

  if (totalPages <= 3) {
    return Array.from({ length: totalPages }, (_, i) => (
      <PaginationItem className="cursor-pointer" key={i}>
        <PaginationLink
          to={getPageHref(i + 1)}
          isActive={currentPage === i + 1}
          onClick={goToPage(i + 1)}
          aria-label={`Go to page ${i + 1}${currentPage === i + 1 ? " (current page)" : ""}`}
        >
          {i + 1}
        </PaginationLink>
      </PaginationItem>
    ));
  }

  const firstPage = (
    <PaginationItem key="first" className="cursor-pointer">
      <PaginationLink
        to={getPageHref(1)}
        isActive={currentPage === 1}
        onClick={goToPage(1)}
        aria-label={`Go to page 1${currentPage === 1 ? " (current page)" : ""}`}
      >
        1
      </PaginationLink>
    </PaginationItem>
  );

  const lastPage = (
    <PaginationItem key="last" className="cursor-pointer">
      <PaginationLink
        to={getPageHref(totalPages)}
        isActive={currentPage === totalPages}
        onClick={goToPage(totalPages)}
        aria-label={`Go to page ${totalPages}${currentPage === totalPages ? " (current page)" : ""}`}
      >
        {new Intl.NumberFormat().format(totalPages)}
      </PaginationLink>
    </PaginationItem>
  );

  if (currentPage === 1) {
    return (
      <>
        {firstPage}
        <PaginationItem className="cursor-pointer">
          <PaginationLink to={getPageHref(2)} onClick={goToPage(2)} aria-label="Go to page 2">
            2
          </PaginationLink>
        </PaginationItem>
        <PaginationItem>
          <PaginationEllipsis />
        </PaginationItem>
        {lastPage}
      </>
    );
  }

  if (currentPage > totalPages - 2) {
    return (
      <>
        {firstPage}
        <PaginationItem>
          <PaginationEllipsis />
        </PaginationItem>
        <PaginationItem className="cursor-pointer">
          <PaginationLink
            to={getPageHref(totalPages - 2)}
            onClick={goToPage(totalPages - 2)}
            aria-label={`Go to page ${totalPages - 2}`}
          >
            {totalPages - 2}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem className="cursor-pointer">
          <PaginationLink
            to={getPageHref(totalPages - 1)}
            isActive={currentPage === totalPages - 1}
            onClick={goToPage(totalPages - 1)}
            aria-label={`Go to page ${totalPages - 1}${currentPage === totalPages - 1 ? " (current page)" : ""}`}
          >
            {totalPages - 1}
          </PaginationLink>
        </PaginationItem>
        {lastPage}
      </>
    );
  }

  return (
    <>
      {firstPage}
      <PaginationItem>
        <PaginationEllipsis />
      </PaginationItem>
      <PaginationItem className="cursor-pointer">
        <PaginationLink
          to={getPageHref(currentPage)}
          isActive
          onClick={goToPage(currentPage)}
          aria-label={`Go to page ${currentPage} (current page)`}
        >
          {currentPage}
        </PaginationLink>
      </PaginationItem>
      <PaginationItem className="cursor-pointer">
        <PaginationLink
          to={getPageHref(currentPage + 1)}
          onClick={goToPage(currentPage + 1)}
          aria-label={`Go to page ${currentPage + 1}`}
        >
          {currentPage + 1}
        </PaginationLink>
      </PaginationItem>
      {lastPage}
    </>
  );
};

export default Pagination;
